import { useEffect, useRef, useState } from "react";
import { Icon } from "@astrum/ui";
import { brandInitials } from "../lib/branding.js";
import { useWorkspace } from "../lib/workspace.js";
import "./workspace-switcher.css";

/**
 * The office switcher, anchored to the nav rail.
 *
 * Only relevant to someone who belongs to more than one office — with a single
 * office there is nothing to switch to, so this renders nothing at all. Cloned
 * from the notifications panel's idiom: a rail-aligned trigger opening a
 * popover that dismisses on click-outside or Escape.
 */
export function WorkspaceSwitcher({ railExpanded }: { railExpanded: boolean }) {
  const { workspaces, activeId, active, setActive } = useWorkspace();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  /* Escape and click-outside, matching Notifications — a popover you can only
   * close by finding the button that opened it is a popover people leave open. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  // Nothing to switch between, so nothing to show. AppShell renders this
  // unconditionally and lets the guard live here, keeping it self-contained.
  if (workspaces.length <= 1) return null;

  const label = active?.name ?? "…";

  return (
    <div className="workspace-switcher" ref={wrapRef}>
      <button
        type="button"
        className={`workspace-switcher__trigger shell-rail-action ${open ? "is-open" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Office: ${label} — switch office`}
        title={railExpanded ? undefined : label}
        onClick={() => setOpen((v) => !v)}
      >
        {railExpanded ? (
          <>
            <span className="workspace-switcher__glyph" aria-hidden="true">
              {active ? brandInitials(active.name) : <Icon name="home" size={16} />}
            </span>
            <span className="shell-nav-label">{label}</span>
            <Icon name="chevron-down" size={16} />
          </>
        ) : (
          <>
            <span className="workspace-switcher__glyph" aria-hidden="true">
              {active ? brandInitials(active.name) : <Icon name="home" size={18} />}
            </span>
            <span className="shell-tip">{label}</span>
          </>
        )}
      </button>

      {open && (
        <div className="workspace-switcher__panel" role="dialog" aria-label="Switch office">
          {workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              className={`workspace-switcher__item ${w.id === activeId ? "is-active" : ""}`}
              onClick={() => setActive(w.id)}
            >
              <span className="workspace-switcher__item-text">
                <strong>{w.name}</strong>
                <span>{w.role}</span>
              </span>
              {w.id === activeId && <Icon name="check" size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
